import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useSnack } from "../../services/SnakbarProvider";
import { getProviderList } from "../../api/providers";
import { handleError } from "../../services/errorHandler";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { FastField, Field, Form, Formik } from "formik";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import { getPlantList } from "../../api/enterprise";
import { useNavigate } from "react-router-dom";
import { getCurrencyList } from "../../api/common/currencies";
import {
  addPurchaseOrder,
  getPurchaseOrderDetail,
  updatePurchaseOrderHeader,
} from "../../api/purchaseOrders";
import { getUserCompany } from "../../services/auth";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import { getCatalogue } from "../../api/common/catalogues.ts";

const MATERIAL_CONSUMABLE_CODES = ["POTYPE_MATERIAL", "POTYPE_CONSUMABLE"];
const nationalNote = "FAVOR DE ELABORAR LA FACTURA EN LA FORMA PPD"
const importNote = "FAVOR DE ELABORAR LA FACTURA EN LA FORMA PPD \nCONDICIONES DE PAGO: \n    X% ANTICIPO,\n    X% AL EMBARQUE\n    X% CONTRAENTREGA"


const isMaterialOrConsumableTypeId = (typeId, poTypeOptions) => {
  const code = poTypeOptions.find((type) => String(type.id) === String(typeId))?.code;
  return MATERIAL_CONSUMABLE_CODES.includes(code);
};

const createValidationSchema = (poTypeOptions) =>
  Yup.object().shape({
    providerId: Yup.string().required("El proveedor es obligatorio"),
    purchaseOrderTypeId: Yup.string().required("El tipo de orden es obligatorio"),
    plantId: Yup.number().required("La planta es obligatoria"),
    arrivalEta: Yup.date(),
    requestedFrom: Yup.string().required("El usuario solicitante es obligatorio"),
    currencyId: Yup.number().required("La moneda es obligatoria"),
    noConcepts: Yup.number("Debe ser un número")
      .transform((value, original) => (original === "" || original == null ? undefined : value))
      .when("purchaseOrderTypeId", {
        is: (typeId) => isMaterialOrConsumableTypeId(typeId, poTypeOptions),
        then: (schema) => schema.notRequired(),
        otherwise: (schema) =>
          schema
            .positive("El número de conceptos debe ser mayor que 0")
            .required("El número de conceptos es obligatorio"),
      }),
    notes: Yup.string().required("La nota es obligatoria"),
    isImport: Yup.boolean(),
    incotermId: Yup.number()
      .nullable()
      .transform((value, original) => (original === "" || original == null ? null : value))
      .when("isImport", {
        is: true,
        then: (schema) => schema.required("El Incoterm es obligatorio"),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

export const validationSchema = createValidationSchema([]);

const initialValues = {
  providerId: "",
  purchaseOrderTypeId: "",
  companyId: getUserCompany() ?? "",
  plantId: 8,
  arrivalDate: dayjs().add(1, "day"),
  arrivalEta: dayjs().add(1, "day"),
  requestedFrom: "",
  currencyId: 1,
  noConcepts: 1,
  notes: nationalNote,
  isImport: false,
  incotermId: null,
};

export default function PurchaseOrderForm({
  props,
  companyId,
  onSubmit,
  id,
  duplicate = false,
}) {
  const ID = id || props?.id;
  const formMode = ID ? (duplicate ? "duplicate" : "edit") : "insert";

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialValues);
  const [providerOptions, setProviderOptions] = useState([]);
  const [poTypeOptions, setPoTypeOptions] = useState([]);
  const { openSnack } = useSnack();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getPurchaseOrderDetail(ID);
      const noConcepts = formMode === "edit" && res.data.items.length === 0 ? 1 : res.data.items.length;
      const tomorrow = dayjs().add(1, "day");
      setFormData({
        ...res.data,
        noConcepts,
        ...(formMode === "duplicate"
          ? { arrivalDate: tomorrow, arrivalEta: tomorrow }
          : {}),
      });
    } catch (e) {
      openSnack(handleError(e), "error");
    }
    setLoading(false);
  };

  const fetchProviderOptions = async (inputValue) => {
    try {
      const res = await getProviderList({
        code: inputValue ? inputValue.toUpperCase() : null,
        companyId: companyId,
      });
      setProviderOptions(res.data);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  };

  const generatePayload = (values) => {
    let payload = { ...values };
    delete payload.noConcepts;

    const formattedDate = values.arrivalDate
      ? dayjs(values.arrivalDate).format("YYYY-MM-DD")
      : "";
    const formattedEtaDate = values.arrivalEta
      ? dayjs(values.arrivalEta).format("YYYY-MM-DD")
      : "";
    payload.arrivalDate = formattedDate;
    payload.arrivalEta = formattedEtaDate;
    return payload;
  };

  const handleNavigate = (POdata) => {
    navigate("/purchase-orders/concepts", { state: POdata });
  };

  const handleSubmit = async (values) => {
    let payload = generatePayload(values);
    payload.companyId = companyId;
    try {
      let POdata = {};
      if (formMode == "insert") {
        const res = await addPurchaseOrder(payload);
        POdata = res.data;
        if (MATERIAL_CONSUMABLE_CODES.includes(POdata.purchaseOrderType?.code)) {
          POdata.items = [];
        } else {
          POdata.noConcepts = values.noConcepts;
        }
      } else if (formMode == "edit") {
        let updatePayload = { ...payload };
        updatePayload.companyId = companyId;
        const res = await updatePurchaseOrderHeader(id, updatePayload);
        POdata = res.data;
        POdata.items = payload.items;
        POdata.companyId = companyId;
        handleNavigate(POdata);
      } else if (formMode == "duplicate") {
        delete payload.id;
        // Strip item ids / delivery state so backend creates fresh lines on the new PO
        if (payload.items && Array.isArray(payload.items)) {
          payload.items = payload.items.map((item) => {
            const {
              id,
              purchaseOrderId,
              quantityDelivered,
              expenseAccount,
              product,
              unit,
              assetType,
              asset,
              ...itemWithoutId
            } = item;
            return {
              ...itemWithoutId,
              productId:
                itemWithoutId.productId && itemWithoutId.productId != 0
                  ? itemWithoutId.productId
                  : null,
            };
          });
        }
        const res = await addPurchaseOrder(payload);
        POdata = res.data;
        // Prefer persisted items from create response; fall back to payload
        POdata.items = res.data.items?.length ? res.data.items : payload.items;
      }
      POdata.companyId = companyId;
      handleNavigate(POdata);
    } catch (e) {
      console.log(e);
      openSnack(handleError(e), "error");
    }
  };

  const fetchPoTypeOptions = async () => {
    try {
      const res = await getCatalogue({ catalogueName: "PURCHASE_ORDER_TYPE" });
      setPoTypeOptions(res.data || []);
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  };

  useEffect(() => {
    fetchProviderOptions();
    fetchPoTypeOptions();
    if (["duplicate", "edit"].includes(formMode)) {
      fetchData();
    } else {
      setFormData(initialValues);
    }
  }, []);


  return loading ? (
    <CircularProgress />
  ) : (
    <Formik
      enableReinitialize
      initialValues={{ ...formData }}
      validationSchema={createValidationSchema(poTypeOptions)}
      onSubmit={handleSubmit}
    >
      {({
        isSubmitting,
        values,
        handleChange,
        handleBlur,
        setFieldValue,
        errors,
        touched,
      }) => (
        <Form>
          <Grid container spacing={2}>
            {formMode == "insert" && (
              <Grid item xs={12}>
                <Typography variant="h4">Nueva orden de compra</Typography>
              </Grid>
            )}
            <Grid item xs={8}>
              {formMode == "insert" && (
                <Autocomplete
                  id="providerId"
                  name="providerId"
                  options={providerOptions}
                  getOptionLabel={(option) =>
                    // `${option.code} | ${option.legalName}` || ""
                    option.code || ""
                  }
                  value={
                    providerOptions.find(
                      (option) => option.id === values.providerId
                    ) || null
                  }
                  onInputChange={(event, value) => fetchProviderOptions(value)}
                  onChange={(event, value) => {
                    setFieldValue("providerId", value ? value.id : "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Proveedor"
                      error={touched.providerId && Boolean(errors.providerId)}
                      helperText={touched.providerId && errors.providerId}
                    />
                  )}
                />
              )}
              {formMode == "edit" && (
                <TextField
                  fullWidth
                  value={formData?.provider?.code || ""}
                  label="Proveedor"
                  disabled
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <Field
                as={DropdownSelector}
                name="purchaseOrderTypeId"
                label="Tipo de orden"
                fullWidth
                fetchFunction={getCatalogue}
                fetchParams={{ catalogueName: "PURCHASE_ORDER_TYPE" }}
                optionLabel="name"
              />
            </Grid>
            <Grid item xs={4}>
              <FastField
                name="plantId"
                label="Planta destino"
                as={DropdownSelector}
                fetchFunction={getPlantList}
                fetchParams={{ companyId: companyId }}
              />
            </Grid>
            <Grid item xs={4}>
              <MyDatePicker
                fullWidth
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: touched.arrivalEta && Boolean(errors.arrivalEta),
                    helperText: touched.arrivalEta && errors.arrivalEta
                  }
                }}
                label="Fecha estimada de entrega"
                value={values.arrivalEta ? dayjs(values.arrivalEta) : null}
                onChange={(value) => setFieldValue("arrivalEta", value)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>

            <Grid item xs={2}>
              <Field
                as={DropdownSelector}
                name="currencyId"
                label="Moneda"
                fullWidth
                fetchFunction={getCurrencyList}
                optionLabel="symbol"
              />
            </Grid>
            {!isMaterialOrConsumableTypeId(values.purchaseOrderTypeId, poTypeOptions) && (
              <Grid item xs={2}>
                <Field
                  fullWidth
                  name={"noConcepts"}
                  label="No. de conceptos"
                  as={TextField}
                  error={touched.noConcepts && Boolean(errors.noConcepts)}
                  helperText={touched.noConcepts && errors.noConcepts}
                />
              </Grid>
            )}
            <Grid item xs={4}>
              <Field
                as={TextField}
                name="requestedFrom"
                label="Pedido por"
                fullWidth
                error={touched.requestedFrom && !!errors.requestedFrom}
                helperText={touched.requestedFrom && errors.requestedFrom}
              ></Field>
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isImport"
                    checked={Boolean(values.isImport)}
                    onChange={(e) => {
                      const nextIsImport = e.target.checked;
                      setFieldValue("isImport", nextIsImport);
                      setFieldValue("notes", nextIsImport ? importNote : nationalNote);
                      if (!nextIsImport) {
                        setFieldValue("incotermId", null);
                      }
                    }}
                  />
                }
                label="Importación"
              />
            </Grid>
            {values.isImport && (
              <Grid item xs={4}>
                <Field
                  as={DropdownSelector}
                  name="incotermId"
                  label="Incoterm"
                  fullWidth
                  fetchFunction={getCatalogue}
                  fetchParams={{ catalogueName: "INCOTERM" }}
                  optionLabel="name"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Field
                name="notes"
                label="Notas"
                as={TextField}
                multiline
                rows={6}
                sx={{ width: "100%" }}
                error={touched.notes && Boolean(errors.notes)}
                helperText={touched.notes && errors.notes}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={() => {
                  if (Object.keys(errors).length > 0) {
                    console.log("Validation Errors:", errors);
                  }
                }}
              >
                Guardar
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}
